/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, useMemo } from 'react';
import type { ShopperProducts } from '@/scapi';
import { getDisplayVariationValues, type EnrichedProductItem } from '@/lib/product/product-utils';
import { cn } from '@/lib/utils';

interface KitItemVariationsProps {
    items: EnrichedProductItem[];
    masterProduct?: ShopperProducts.schemas['Product'];
    kitTitle?: string;
    className?: string;
}

interface SFCCVariationObject {
    displayLabel?: string;
    displayValue?: string;
    name?: string;
    id?: string;
    value?: string;
}

type NormalizedVariationAttribute = {
    id?: string;
    name?: string;
    values?: Array<{ name?: string; value?: string }>;
};

const TARGET_KEYS = ['color', 'size', 'size2', 'bedSize', 'c_color', 'c_size', 'c_size2', 'c_bedSize'] as const;
const SPLIT_SIZE_REGEX = /\b(split\s+(california\s+king|king|queen|full|twin\s*xl|twin))\b/i;
const SIZE_LABEL = 'Size';
const COLOR_LABEL = 'Color';
const KIT_TITLE_FALLBACK_ID = 'kit-title-fallback';

function getAttributesList(
    item: EnrichedProductItem,
    masterProduct: ShopperProducts.schemas['Product'] | undefined
): NormalizedVariationAttribute[] {
    return (item.variationAttributes || masterProduct?.variationAttributes || []) as NormalizedVariationAttribute[];
}

function toVariationEntries(variations: unknown): [string, string][] {
    if (variations && Array.isArray(variations)) {
        return (variations as SFCCVariationObject[]).map((v) => [
            v.displayLabel || v.name || v.id || '',
            v.displayValue || v.value || '',
        ]);
    }

    if (variations && typeof variations === 'object') {
        return Object.entries(variations as Record<string, string>);
    }

    return [];
}

function isSizeLabel(label: string): boolean {
    const normalized = label.toLowerCase();
    return normalized.includes('size') || normalized.includes('bed');
}

function isColorLabel(label: string): boolean {
    return label.toLowerCase().includes('color');
}

function toNormalizedText(value: string): string {
    return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function toNonEmptyString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function extractVariationValue(value: unknown): string | undefined {
    const direct = toNonEmptyString(value);
    if (direct) return direct;
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        return toNonEmptyString(record.displayValue) || toNonEmptyString(record.value) || toNonEmptyString(record.name);
    }
    return undefined;
}

function upsertVariationEntry(
    variationEntries: [string, string][],
    labelMatcher: (label: string) => boolean,
    fallbackLabel: string,
    value: string,
    insertAtStart = false
): void {
    const existingIndex = variationEntries.findIndex(([label]) => labelMatcher(label));
    if (existingIndex >= 0) {
        variationEntries[existingIndex] = [variationEntries[existingIndex]?.[0] || fallbackLabel, value];
        return;
    }
    if (insertAtStart) {
        variationEntries.unshift([fallbackLabel, value]);
        return;
    }
    variationEntries.push([fallbackLabel, value]);
}

/**
 * Resolves a raw variation attribute VALUE (e.g. `'california-king'`,
 * `'linen'`) to its display NAME (e.g. `'California King'`, `'Linen'`) via
 * the matching variation attribute's `values[]`. Falls back to the raw value
 * unchanged when no attribute/value match is found — covers custom
 * attributes (`size`, `bedSize`, etc.) that aren't declared as variation
 * attributes, which are typically already human-readable strings.
 *
 * Shared by both size (`readPreferredExplicitSize`) and color
 * (`readPreferredExplicitColor`) resolution — the lookup is identical
 * regardless of which attribute family is being resolved.
 */
function resolveAttributeDisplayValue(
    attributeId: string,
    rawValue: string | undefined,
    attrs: NormalizedVariationAttribute[]
): string | undefined {
    if (!rawValue) return undefined;
    const attribute = attrs.find((attr) => (attr.id ?? '').toLowerCase() === attributeId.toLowerCase());
    const matchedValue = attribute?.values?.find((value) => value.value === rawValue);
    return matchedValue?.name || rawValue;
}

function readPreferredExplicitSize(
    item: EnrichedProductItem,
    attrs: NormalizedVariationAttribute[]
): string | undefined {
    const raw = item as unknown as Record<string, unknown>;
    const customAttributes = (raw.customAttributes as Record<string, unknown> | undefined) || {};
    const variationValues = (raw.variationValues as Record<string, unknown> | undefined) || {};

    return (
        toNonEmptyString(raw.c_size2) ||
        toNonEmptyString(customAttributes.c_size2) ||
        extractVariationValue(variationValues.c_size2) ||
        toNonEmptyString(raw.c_size) ||
        toNonEmptyString(customAttributes.c_size) ||
        extractVariationValue(variationValues.c_size) ||
        resolveAttributeDisplayValue(
            'size2',
            toNonEmptyString(raw.size2) || extractVariationValue(variationValues.size2),
            attrs
        ) ||
        resolveAttributeDisplayValue(
            'bedSize',
            toNonEmptyString(raw.bedSize) || extractVariationValue(variationValues.bedSize),
            attrs
        ) ||
        resolveAttributeDisplayValue(
            'size',
            toNonEmptyString(raw.size) || extractVariationValue(variationValues.size),
            attrs
        )
    );
}

function readPreferredExplicitColor(
    item: EnrichedProductItem,
    masterProduct: ShopperProducts.schemas['Product'] | undefined
): string | undefined {
    const raw = item as unknown as Record<string, unknown>;
    const rawMaster = (masterProduct || {}) as Record<string, unknown>;
    const customAttributes = (raw.customAttributes as Record<string, unknown> | undefined) || {};
    const variationValues = (raw.variationValues as Record<string, unknown> | undefined) || {};
    const attrs = getAttributesList(item, masterProduct);

    return (
        toNonEmptyString(raw.c_color) ||
        toNonEmptyString(rawMaster.c_color) ||
        toNonEmptyString(customAttributes.c_color) ||
        extractVariationValue(variationValues.c_color) ||
        resolveAttributeDisplayValue(
            'color',
            toNonEmptyString(raw.color) ||
                toNonEmptyString(rawMaster.color) ||
                extractVariationValue(variationValues.color),
            attrs
        ) ||
        toNonEmptyString(raw.c_color2) ||
        toNonEmptyString(rawMaster.c_color2) ||
        toNonEmptyString(customAttributes.c_color2) ||
        extractVariationValue(variationValues.c_color2)
    );
}

function resolveSizeFromKitTitle(
    kitTitle: string | undefined,
    currentSizeValue: string | undefined,
    attrs: NormalizedVariationAttribute[]
): string | undefined {
    if (!kitTitle) return undefined;
    const normalizedTitle = toNormalizedText(kitTitle);

    if (currentSizeValue) {
        const normalizedCurrent = toNormalizedText(currentSizeValue);
        if (normalizedCurrent && normalizedTitle.includes(`split ${normalizedCurrent}`)) {
            return `Split ${currentSizeValue}`;
        }
        // A size was already resolved for this item and the title doesn't
        // indicate a split variant of it — don't let the generic
        // attrCandidate/regex fallback below override it with an unrelated
        // title match (it exists only to derive a size when none is known).
        return undefined;
    }

    const attrCandidate = attrs
        .filter((attr) => {
            const key = `${attr.id ?? ''} ${attr.name ?? ''}`.toLowerCase();
            return key.includes('size') || key.includes('bed');
        })
        .flatMap((attr) => attr.values?.map((value) => value.name || value.value).filter(Boolean) ?? [])
        .filter((value): value is string => typeof value === 'string')
        .sort((a, b) => b.length - a.length)
        .find((candidate) => normalizedTitle.includes(candidate.toLowerCase()));
    if (attrCandidate) return attrCandidate;

    const splitMatch = kitTitle.match(SPLIT_SIZE_REGEX);
    return splitMatch?.[1]
        ?.replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function KitItemVariations({
    items,
    masterProduct,
    kitTitle,
    className,
}: KitItemVariationsProps): ReactElement | null {
    const processedItems = useMemo(() => {
        const stableFallbackIdBase = kitTitle || masterProduct?.id || KIT_TITLE_FALLBACK_ID;
        const processed = items
            .map((item, itemIndex) => {
                const variations =
                    item.displayVariations ||
                    getDisplayVariationValues(
                        item.variationAttributes || masterProduct?.variationAttributes,
                        item.variationValues
                    );
                const variationEntries = toVariationEntries(variations);

                const shouldBackfillTargetKeys =
                    variationEntries.length === 0 ||
                    !variationEntries.some(([label]) => isSizeLabel(label)) ||
                    !variationEntries.some(([label]) => isColorLabel(label));

                if (shouldBackfillTargetKeys) {
                    const rawItem = item as Record<string, unknown>;
                    const customAttributes = (rawItem.customAttributes as Record<string, unknown>) || {};
                    const variationValues = (rawItem.variationValues as Record<string, Record<string, unknown>>) || {};
                    const attributesList = getAttributesList(item, masterProduct) as Array<Record<string, unknown>>;
                    TARGET_KEYS.forEach((key) => {
                        let val =
                            rawItem[key] ||
                            customAttributes[key] ||
                            variationValues[key] ||
                            (variationValues[key] as { value?: unknown } | undefined)?.value;

                        if (!val || typeof val === 'number') {
                            const richTextSource = String(
                                rawItem.itemText ||
                                    rawItem.c_consumerDescription ||
                                    rawItem.c_kitDisplayName ||
                                    rawItem.productName ||
                                    rawItem.name ||
                                    kitTitle ||
                                    ''
                            );
                            const richTextLower = richTextSource.toLowerCase();

                            const lowerKey = key.toLowerCase();

                            const matchedAttr = attributesList.find((attr) => {
                                const attrId = String(attr.id || '').toLowerCase();
                                return (
                                    (attrId.includes('size') && lowerKey.includes('size')) ||
                                    (attrId.includes('color') && lowerKey.includes('color'))
                                );
                            });

                            const matchedAttrValues = matchedAttr?.values;
                            if (Array.isArray(matchedAttrValues)) {
                                const foundValueObj = matchedAttrValues.find((v) => {
                                    const valueObj = (v || {}) as Record<string, unknown>;
                                    const optionText = String(valueObj.value || valueObj.name || '');
                                    return optionText && richTextLower.includes(optionText.toLowerCase());
                                });

                                if (foundValueObj) {
                                    const valueObj = foundValueObj as Record<string, unknown>;
                                    val = valueObj.value || valueObj.name;
                                }
                            }
                        }

                        if (val && typeof val === 'string') {
                            const lowerKey = key.toLowerCase();
                            let formattedLabel = SIZE_LABEL;
                            if (lowerKey.includes('color')) {
                                formattedLabel = COLOR_LABEL;
                            }

                            const labelExists = variationEntries.some(([l]) => l === formattedLabel);
                            const valueExists = variationEntries.some(([, v]) => v === String(val));

                            if (!labelExists && !valueExists) {
                                variationEntries.push([formattedLabel, String(val)]);
                            }
                        }
                    });
                }

                if (kitTitle) {
                    const attrs = getAttributesList(item, masterProduct);
                    const existingSizeEntry = variationEntries.find(([label]) => {
                        return isSizeLabel(label);
                    });
                    const explicitSize = readPreferredExplicitSize(item, attrs);
                    const titleMatchedSize = resolveSizeFromKitTitle(
                        kitTitle,
                        explicitSize || existingSizeEntry?.[1],
                        attrs
                    );
                    // Prefer the title-driven result — it already factors in
                    // `explicitSize`/`existingSizeEntry` via the "split
                    // <current value>" check above and only returns a value
                    // when it found either that split enhancement or (absent
                    // any known size) a generic title match.
                    const resolvedSize = titleMatchedSize || explicitSize;

                    if (resolvedSize) {
                        upsertVariationEntry(variationEntries, isSizeLabel, SIZE_LABEL, resolvedSize, true);
                    }
                }

                const explicitColor = readPreferredExplicitColor(item, masterProduct);
                if (explicitColor) {
                    upsertVariationEntry(variationEntries, isColorLabel, COLOR_LABEL, explicitColor);
                }

                return {
                    id: item.itemId || item.productId || `${stableFallbackIdBase}-${itemIndex}`,
                    entries: variationEntries,
                };
            })
            .filter((item) => item.entries.length > 0);
        if (processed.length > 0) return processed;

        const titleText = String(kitTitle || '').toLowerCase();
        const attrs = (masterProduct?.variationAttributes || []) as NormalizedVariationAttribute[];

        const sizeCandidates = attrs
            .filter((attr) => {
                const key = `${attr.id ?? ''} ${attr.name ?? ''}`.toLowerCase();
                return key.includes('size') || key.includes('bed');
            })
            .flatMap((attr) => attr.values?.map((value) => value.name || value.value).filter(Boolean) ?? [])
            .filter((value): value is string => typeof value === 'string')
            .sort((a, b) => b.length - a.length);
        const titleSize = sizeCandidates.find((candidate) => titleText.includes(candidate.toLowerCase()));
        const masterColor = (masterProduct as unknown as Record<string, unknown>)?.c_color;
        const fallbackEntries: [string, string][] = [];
        if (titleSize) fallbackEntries.push([SIZE_LABEL, titleSize]);
        if (typeof masterColor === 'string' && masterColor.trim()) {
            fallbackEntries.push([COLOR_LABEL, masterColor.trim()]);
        }
        if (fallbackEntries.length === 0) return processed;
        return [{ id: `${stableFallbackIdBase}-title-fallback`, entries: fallbackEntries }];
    }, [items, masterProduct, kitTitle]);

    if (processedItems.length === 0) return null;
    const flattenedEntries = processedItems.flatMap((item) => item.entries);
    const selectedEntries: Array<[string, string]> = [];
    let hasSize = false;
    let hasColor = false;
    const seenValues = new Set<string>();

    flattenedEntries.forEach(([label, value]) => {
        if (isSizeLabel(label)) {
            if (hasSize) return;
            hasSize = true;
            selectedEntries.push([label, value]);
            seenValues.add(value);
            return;
        }

        if (isColorLabel(label)) {
            if (hasColor) return;
            hasColor = true;
            selectedEntries.push([label, value]);
            seenValues.add(value);
            return;
        }

        if (seenValues.has(value)) return;
        seenValues.add(value);
        selectedEntries.push([label, value]);
    });

    // Final canonicalization guard: prefer master-level canonical color when
    // present (e.g. c_color=Gray) over any component-level fallback shades
    // like c_color2=Brown/Beige.
    const preferredMasterColor = toNonEmptyString((masterProduct as Record<string, unknown> | undefined)?.c_color);
    if (preferredMasterColor) {
        upsertVariationEntry(selectedEntries, isColorLabel, COLOR_LABEL, preferredMasterColor);
    }

    const uniqueValues = selectedEntries.map(([, value]) => value);

    if (uniqueValues.length === 0) return null;

    return (
        <div
            className={cn(
                'mt-1.5 flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-muted-foreground',
                className
            )}>
            {uniqueValues.map((value, idx) => (
                <div key={value} className="inline-flex items-center gap-1.5">
                    <span>{value}</span>
                    {idx < uniqueValues.length - 1 && (
                        <span className="text-xs text-muted-foreground" aria-hidden="true">
                            &middot;
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}
